import type { SymbolCommand } from './symbolShape';

/**
 * The 24 × 24 geometry as SVG path data.
 *
 * Mirrors `SymbolPainter.kt` and `SymbolPainter.swift` — an interpreter of about forty lines, deliberately
 * tiny. All the judgement about what a horse looks like lives in the catalogue, which all three clients
 * read from the same numbers.
 */
export function toPathData(commands: SymbolCommand[]): string {
  const parts: string[] = [];

  // SVG requires a path to open with a moveto; Compose's `Path` and SwiftUI's both begin implicitly at the
  // origin instead. One catalogue path — `hook`'s second stroke — starts with a curve, and without this it
  // is dropped entirely by the browser while the two apps draw it from (0,0).
  //
  // Matching the apps is the point, artefact included. That the shape starts at the corner at all looks
  // like a defect in the catalogue rather than in any renderer, and correcting geometry is a design-system
  // change with its own process (13-Changing-This-System) — not something to do quietly in one client.
  if (commands.length > 0 && commands[0].kind !== 'moveTo') parts.push('M 0 0');

  for (const command of commands) {
    switch (command.kind) {
      case 'moveTo':
        parts.push(`M ${command.x} ${command.y}`);
        break;
      case 'lineTo':
        parts.push(`L ${command.x} ${command.y}`);
        break;
      case 'curveTo':
        parts.push(`C ${command.c1x} ${command.c1y} ${command.c2x} ${command.c2y} ${command.x} ${command.y}`);
        break;
      case 'close':
        parts.push('Z');
        break;
      case 'circle': {
        const { cx, cy, radius } = command;

        // SVG has no circle command inside path data, so two half-arcs. Identical geometry to Compose's
        // addOval and SwiftUI's addEllipse.
        parts.push(
          `M ${cx - radius} ${cy}`,
          `a ${radius} ${radius} 0 1 0 ${radius * 2} 0`,
          `a ${radius} ${radius} 0 1 0 ${-radius * 2} 0`,
          'Z',
        );
        break;
      }
    }
  }

  return parts.join(' ');
}
